/** Structured tracing for synth diagnostics.
 *
 * Emits timestamped, JSON-parseable log lines under the `[synth-trace]` prefix.
 * Playwright tests collect these via `page.on("console")` for post-hoc analysis.
 *
 * Enable tracing by setting `window.__SYNTH_TRACE = true` before init,
 * or by calling `enableSynthTrace()`.
 */

/** A single trace event emitted by the synth pipeline. */
export type SynthTraceEvent = {
	/** Monotonic timestamp (ms) from performance.now(). */
	readonly ts: number;
	/** Subsystem that produced the event. */
	readonly src: "keyboard" | "hook" | "controller" | "audio";
	/** Short event name, e.g. "noteOn", "voiceSteal", "gate". */
	readonly evt: string;
	/** Arbitrary structured payload. */
	readonly data?: Record<string, unknown>;
};

/** Prefix used in console output so Playwright can filter trace lines. */
const TRACE_PREFIX = "[synth-trace]" as const;

/** Check whether tracing is currently enabled. */
function isEnabled(): boolean {
	try {
		return (
			typeof window !== "undefined" &&
			(window as unknown as Record<string, unknown>).__SYNTH_TRACE === true
		);
	} catch {
		return false;
	}
}

/** Enable synth tracing globally. */
export function enableSynthTrace(): void {
	if (typeof window !== "undefined") {
		(window as unknown as Record<string, unknown>).__SYNTH_TRACE = true;
	}
}

/** Disable synth tracing globally. */
export function disableSynthTrace(): void {
	if (typeof window !== "undefined") {
		(window as unknown as Record<string, unknown>).__SYNTH_TRACE = false;
	}
}

/** Emit a trace event to the console. No-op when tracing is disabled. */
export function trace(
	src: SynthTraceEvent["src"],
	evt: string,
	data?: Record<string, unknown>,
): void {
	if (!isEnabled()) return;
	const event: SynthTraceEvent = {
		ts: performance.now(),
		src,
		evt,
		...(data !== undefined ? { data } : {}),
	};
	// Use console.info so it doesn't get filtered out by Playwright's
	// default error-only collection, and is easy to grep.
	console.info(TRACE_PREFIX, JSON.stringify(event));
}
